import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/endpoints';
import type { HistorySession, SessionHistory, Station } from '../api/types';
import {
  formatDateTime,
  formatMinutes,
  formatMoney,
  formatTime,
  owedAfterCredit,
  SOURCE_LABEL,
} from '../lib/format';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Input,
  Modal,
  Select,
  Spinner,
} from '../components/ui';

const PAGE_SIZE = 50;
const GROUPED_VIEW_PAGE_SIZE = 200;

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

type SessionHistoryRow = {
  key: string;
  firstSession: HistorySession;
  sessions: HistorySession[];
  totalMinutes: number;
};

function sessionTime(value: string | null): number {
  return value ? new Date(value).getTime() : 0;
}

function hasDateFilter(from: string, to: string): boolean {
  return Boolean(from || to);
}

function rowsFromSessions(sessions: HistorySession[]): SessionHistoryRow[] {
  return sessions.map((session) => ({
    key: session.session_id,
    firstSession: session,
    sessions: [session],
    totalMinutes: session.minutes_deducted,
  }));
}

function localDateKey(value: string): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value.slice(0, 10);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function groupSessionsByDayCustomerStation(
  sessions: HistorySession[],
): SessionHistoryRow[] {
  const groups = new Map<string, HistorySession[]>();

  for (const session of sessions) {
    const key = `${localDateKey(session.start_time)}:${session.customer_id}:${session.station_id}`;
    const group = groups.get(key);

    if (group) group.push(session);
    else groups.set(key, [session]);
  }

  return Array.from(groups.entries())
    .map(([key, group]) => {
      const sorted = [...group].sort(
        (a, b) => sessionTime(a.start_time) - sessionTime(b.start_time),
      );
      return {
        key,
        firstSession: sorted[0],
        sessions: sorted,
        totalMinutes: sorted.reduce(
          (total, session) => total + session.minutes_deducted,
          0,
        ),
      };
    })
    .sort((a, b) => {
      const aLatest = Math.max(
        ...a.sessions.map((session) => sessionTime(session.start_time)),
      );
      const bLatest = Math.max(
        ...b.sessions.map((session) => sessionTime(session.start_time)),
      );
      return bLatest - aLatest;
    });
}

// What the customer still owes now, after their credit. Null on an older
// backend that does not send the tab figures.
function owedNow(session: HistorySession): number | null {
  if (session.customer_due_amount === undefined) return null;
  return owedAfterCredit(session.customer_due_amount, session.customer_credit_balance ?? 0);
}

function EyeIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export function SessionsPage() {
  const [stations, setStations] = useState<Station[]>([]);
  const [stationId, setStationId] = useState('');
  const [dateMode, setDateMode] = useState<'day' | 'range'>('day');
  const [day, setDay] = useState(todayIso());
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(todayIso());
  const [page, setPage] = useState(0);

  const [data, setData] = useState<SessionHistory | null>(null);
  const [detailsRow, setDetailsRow] = useState<SessionHistoryRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api.stations
      .list(controller.signal)
      .then(setStations)
      .catch(() => undefined);
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const filterFrom = dateMode === 'day' ? day : from;
    const filterTo = dateMode === 'day' ? day : to || todayIso();
    const groupedView = hasDateFilter(filterFrom, filterTo);

    setLoading(true);

    async function loadHistory() {
      const baseFilters = {
        station_id: stationId || undefined,
        // The API filters on start_time, so `to` is exclusive — push it to
        // the end of the chosen day so that day's sessions are included.
        from: filterFrom
          ? new Date(`${filterFrom}T00:00:00`).toISOString()
          : undefined,
        to:
          filterFrom || filterTo
            ? new Date(`${filterTo || filterFrom}T23:59:59`).toISOString()
            : undefined,
      };

      if (!groupedView) {
        return api.sessions.history(
          {
            ...baseFilters,
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE,
          },
          controller.signal,
        );
      }

      const firstPage = await api.sessions.history(
        {
          ...baseFilters,
          limit: GROUPED_VIEW_PAGE_SIZE,
          offset: 0,
        },
        controller.signal,
      );

      const sessions = [...firstPage.sessions];
      for (
        let offset = GROUPED_VIEW_PAGE_SIZE;
        offset < firstPage.total_count;
        offset += GROUPED_VIEW_PAGE_SIZE
      ) {
        const nextPage = await api.sessions.history(
          {
            ...baseFilters,
            limit: GROUPED_VIEW_PAGE_SIZE,
            offset,
          },
          controller.signal,
        );
        sessions.push(...nextPage.sessions);
      }

      return { ...firstPage, sessions };
    }

    loadHistory()
      .then((result) => {
        setData(result);
        setDetailsRow(null);
        setError(null);
      })
      .catch((err) => {
        if ((err as Error).name !== 'AbortError')
          setError((err as Error).message);
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [stationId, dateMode, day, from, to, page]);

  const filterFrom = dateMode === 'day' ? day : from;
  const filterTo = dateMode === 'day' ? day : to || todayIso();
  const groupedView = hasDateFilter(filterFrom, filterTo);
  const rows = data
    ? groupedView
      ? groupSessionsByDayCustomerStation(data.sessions)
      : rowsFromSessions(data.sessions)
    : [];
  const totalPages =
    data && !groupedView ? Math.ceil(data.total_count / PAGE_SIZE) : 0;
  // Each customer once, however many of their sessions are listed.
  const owing = new Map<string, number>();
  for (const session of data?.sessions ?? []) {
    const owed = owedNow(session);
    if (owed && owed > 0) owing.set(session.customer_id, owed);
  }
  const owingTotal = [...owing.values()].reduce((sum, owed) => sum + owed, 0);

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Session history</h1>

      <Card className="grid grid-cols-1 gap-3 sm:grid-cols-5">
        <Select
          label="Station"
          value={stationId}
          onChange={(event) => {
            setStationId(event.target.value);
            setPage(0);
          }}
        >
          <option value="">All stations</option>
          {stations.map((station) => (
            <option key={station.station_id} value={station.station_id}>
              {station.name}
            </option>
          ))}
        </Select>
        <div>
          <span className="mb-1.5 block text-sm font-medium text-slate-300">
            View
          </span>
          <div className="grid min-h-11 grid-cols-2 rounded-lg border border-slate-700 bg-slate-900 p-1">
            <button
              type="button"
              onClick={() => {
                setDateMode('day');
                setPage(0);
              }}
              className={`rounded-md px-3 text-sm font-medium transition-colors ${
                dateMode === 'day'
                  ? 'bg-sky-500 text-white'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-slate-100'
              }`}
            >
              One day
            </button>
            <button
              type="button"
              onClick={() => {
                setDateMode('range');
                setTo((current) => current || todayIso());
                setPage(0);
              }}
              className={`rounded-md px-3 text-sm font-medium transition-colors ${
                dateMode === 'range'
                  ? 'bg-sky-500 text-white'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-slate-100'
              }`}
            >
              Range
            </button>
          </div>
        </div>
        {dateMode === 'day' ? (
          <Input
            label="Date"
            type="date"
            value={day}
            onChange={(event) => {
              setDay(event.target.value || todayIso());
              setPage(0);
            }}
          />
        ) : (
          <>
            <Input
              label="From"
              type="date"
              value={from}
              onChange={(event) => {
                setFrom(event.target.value);
                setPage(0);
              }}
            />
            <Input
              label="To"
              type="date"
              value={to || todayIso()}
              onChange={(event) => {
                setTo(event.target.value || todayIso());
                setPage(0);
              }}
            />
          </>
        )}
        <div className="flex items-end">
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => {
              setStationId('');
              setDateMode('day');
              setDay(todayIso());
              setFrom('');
              setTo(todayIso());
              setPage(0);
            }}
          >
            Clear filters
          </Button>
        </div>
      </Card>

      {error && <ErrorNote>{error}</ErrorNote>}

      {owing.size > 0 && (
        <p className="text-sm text-amber-300">
          {owing.size === 1
            ? '1 customer in this list still owes'
            : `${owing.size} customers in this list still owe`}{' '}
          {formatMoney(owingTotal)} in all.
        </p>
      )}

      {loading && !data ? (
        <Spinner />
      ) : !data || data.sessions.length === 0 ? (
        <EmptyState title="No sessions in this range." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-800">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="bg-slate-900 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Started</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Due now</th>
                <th className="px-4 py-3">Station</th>
                <th className="px-4 py-3">Pool</th>
                <th className="px-4 py-3">Played</th>
                <th className="px-4 py-3">Ended</th>
                <th className="px-4 py-3">By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.map((row) => {
                const session = row.firstSession;
                const latestSession = [...row.sessions].sort(
                  (a, b) => sessionTime(b.end_time) - sessionTime(a.end_time),
                )[0];
                const isGrouped = row.sessions.length > 1;

                return (
                  <tr key={row.key} className="hover:bg-slate-900/60">
                    <td className="px-4 py-3 text-slate-300">
                      {formatDateTime(session.start_time)}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/customers/${session.customer_id}`}
                        className="text-sky-300 hover:underline"
                      >
                        {session.customer_name}
                      </Link>
                      <span className="block text-xs text-slate-500">
                        {session.customer_phone}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const owed = owedNow(session);
                        if (owed === null) return <span className="text-slate-600">—</span>;
                        return owed > 0 ? (
                          <Link to={`/customers/${session.customer_id}`}>
                            <Badge tone="amber">{formatMoney(owed)}</Badge>
                          </Link>
                        ) : (
                          <span className="text-xs text-slate-500">Paid up</span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-slate-300">
                      {session.station_name}
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {SOURCE_LABEL[session.source] ?? session.source}
                    </td>
                    <td className="px-4 py-3 text-slate-200">
                      <span className="inline-flex items-center gap-2">
                        {formatMinutes(row.totalMinutes)}
                        {isGrouped && (
                          <button
                            type="button"
                            aria-label={`View ${row.sessions.length} detailed sessions`}
                            title="View detailed history"
                            onClick={() => setDetailsRow(row)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-sky-300 transition-colors hover:bg-sky-500/10 hover:text-sky-200"
                          >
                            <EyeIcon />
                          </button>
                        )}
                      </span>
                      {isGrouped && (
                        <span className="block text-xs text-slate-500">
                          {row.sessions.length} visits combined
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {formatTime(latestSession.end_time)}
                      <span className="block text-xs text-slate-600">
                        {isGrouped
                          ? 'Latest visit'
                          : (session.end_reason ?? '—')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {session.started_by_name ?? 'Signed in at PC'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button
            variant="secondary"
            disabled={page === 0}
            onClick={() => setPage((current) => current - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-slate-500">
            Page {page + 1} of {totalPages} · {data?.total_count} sessions
          </span>
          <Button
            variant="secondary"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {detailsRow && (
        <Modal
          title={`${detailsRow.firstSession.customer_name} at ${detailsRow.firstSession.station_name}`}
          onClose={() => setDetailsRow(null)}
          wide
        >
          <div className="mb-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
            <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
              <p className="text-xs text-slate-500">Combined time</p>
              <p className="mt-1 font-medium text-slate-100">
                {formatMinutes(detailsRow.totalMinutes)}
              </p>
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
              <p className="text-xs text-slate-500">Visits</p>
              <p className="mt-1 font-medium text-slate-100">
                {detailsRow.sessions.length}
              </p>
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
              <p className="text-xs text-slate-500">Pool</p>
              <p className="mt-1 font-medium text-slate-100">
                {SOURCE_LABEL[detailsRow.firstSession.source] ??
                  detailsRow.firstSession.source}
              </p>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="bg-slate-950 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Started</th>
                  <th className="px-4 py-3">Ended</th>
                  <th className="px-4 py-3">Played</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3">By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {detailsRow.sessions.map((session) => (
                  <tr key={session.session_id}>
                    <td className="px-4 py-3 text-slate-300">
                      {formatDateTime(session.start_time)}
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {formatTime(session.end_time)}
                    </td>
                    <td className="px-4 py-3 text-slate-200">
                      {formatMinutes(session.minutes_deducted)}
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {session.end_reason ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {session.started_by_name ?? 'Signed in at PC'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
    </div>
  );
}
